import { Helmet } from "react-helmet-async";

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  ogUrl?: string;
  url?: string; // alias for canonical/ogUrl
  keywords?: string;
}

/**
 * Ensures URL has NO trailing slash (except for root "/")
 * This matches our canonical URL format for consistency
 */
const stripTrailingSlash = (url: string | undefined): string | undefined => {
  if (!url) return undefined;
  // Don't strip trailing slash from root URL
  if (url === "https://www.leafrushmarketing.com/") return url;
  if (url.endsWith("/")) {
    return url.slice(0, -1);
  }
  return url;
};

export const SEO = ({ 
  title, 
  description, 
  canonical,
  ogImage = "https://www.leafrushmarketing.com/og-image.jpg",
  ogUrl,
  url,
  keywords = "lawn care websites, landscaping websites, lawn care marketing, landscaping marketing"
}: SEOProps) => {
  const fullTitle = title.includes("| Leaf Rush") ? title : `${title} | Leaf Rush Marketing`;
  // Strip trailing slashes on all URLs for consistency (except root)
  const resolvedCanonical = stripTrailingSlash(canonical || url);
  const resolvedOgUrl = stripTrailingSlash(ogUrl || url || canonical);
  
  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="robots" content="index, follow" />
      {resolvedCanonical && <link rel="canonical" href={resolvedCanonical} />}
      
      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      {resolvedOgUrl && <meta property="og:url" content={resolvedOgUrl} />}
      <meta property="og:image" content={ogImage} />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@LeafRushMarketing" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
    </Helmet>
  );
};
